import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Refunds.css';
import {
  FaUndoAlt, FaCheckCircle, FaTimesCircle, FaClock, FaExclamationTriangle,
  FaSearch, FaStripe, FaUser, FaUserNurse, FaCalendarAlt, FaDollarSign,
  FaInfoCircle, FaChevronDown
} from 'react-icons/fa';

const STATUS_CONFIG = {
  PENDING:   { label: 'Pending Review', color: '#f59e0b', bg: '#fffbeb', icon: <FaClock /> },
  APPROVED:  { label: 'Approved',       color: '#3b82f6', bg: '#eff6ff', icon: <FaCheckCircle /> },
  REJECTED:  { label: 'Rejected',       color: '#ef4444', bg: '#fef2f2', icon: <FaTimesCircle /> },
  COMPLETED: { label: 'Completed',      color: '#10b981', bg: '#ecfdf5', icon: <FaCheckCircle /> },
  FAILED:    { label: 'Failed',         color: '#dc2626', bg: '#fef2f2', icon: <FaExclamationTriangle /> },
};

const TYPE_CONFIG = {
  FULL:    { label: 'Full Refund',    color: '#0ea5e9' },
  PARTIAL: { label: 'Partial Refund', color: '#f59e0b' },
  CANCEL:  { label: 'Cancellation',   color: '#8b5cf6' },
};

const RefundStatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  return (
    <span className="refund-badge" style={{ color: cfg.color, backgroundColor: cfg.bg }}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

const RefundTypeBadge = ({ type }) => {
  if (!type) return <span className="refund-type-badge refund-type-none">—</span>;
  const cfg = TYPE_CONFIG[type] || {};
  return (
    <span className="refund-type-badge" style={{ color: cfg.color, borderColor: cfg.color }}>
      {cfg.label}
    </span>
  );
};

// ─── Approve Modal ─────────────────────────────────────────────
const ApproveModal = ({ refund, onClose, onConfirm }) => {
  const [refundType, setRefundType] = useState('FULL');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [reverseTransfer, setReverseTransfer] = useState(false);
  const [loading, setLoading] = useState(false);

  const totalAmount = refund?.booking?.totalAmount || refund?.requestedAmount || 0;

  const handleSubmit = async () => {
    if (refundType === 'PARTIAL' && (!approvedAmount || parseFloat(approvedAmount) <= 0)) {
      toast.error('Please enter a valid partial refund amount');
      return;
    }
    setLoading(true);
    await onConfirm({
      refundType,
      approvedAmount: refundType === 'PARTIAL' ? parseFloat(approvedAmount) : undefined,
      adminNote: adminNote || undefined,
      reverseTransfer,
    });
    setLoading(false);
  };

  return (
    <div className="refund-modal-overlay" onClick={onClose}>
      <div className="refund-modal" onClick={e => e.stopPropagation()}>
        <div className="refund-modal-header">
          <div className="refund-modal-title">
            <FaCheckCircle className="modal-icon approve" />
            <h3>Approve Refund</h3>
          </div>
          <button className="refund-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="refund-modal-body">
          <div className="refund-detail-row">
            <span>Booking #</span>
            <strong>{refund?.booking?.bookingNumber || refund?.bookingId}</strong>
          </div>
          <div className="refund-detail-row">
            <span>Customer</span>
            <strong>{refund?.customer?.firstName} {refund?.customer?.lastName}</strong>
          </div>
          <div className="refund-detail-row">
            <span>Total Paid</span>
            <strong className="amount-green">${Number(totalAmount).toFixed(2)}</strong>
          </div>
          <div className="refund-detail-row">
            <span>Customer's Reason</span>
            <em>{refund?.reason}</em>
          </div>

          <div className="form-group mt-4">
            <label>Refund Type *</label>
            <select className="form-control" value={refundType} onChange={e => setRefundType(e.target.value)}>
              <option value="FULL">Full Refund (100% — ${Number(totalAmount).toFixed(2)})</option>
              <option value="PARTIAL">Partial Refund (custom amount)</option>
              <option value="CANCEL">Cancellation Refund (full amount)</option>
            </select>
          </div>

          {refundType === 'PARTIAL' && (
            <div className="form-group">
              <label>Approved Refund Amount ($) *</label>
              <input
                type="number"
                className="form-control"
                placeholder={`Max: ${Number(totalAmount).toFixed(2)}`}
                value={approvedAmount}
                onChange={e => setApprovedAmount(e.target.value)}
                max={totalAmount}
                min="0.01"
                step="0.01"
              />
            </div>
          )}

          <div className="form-group">
            <label>Admin Note (optional)</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Internal note shown to customer..."
              value={adminNote}
              onChange={e => setAdminNote(e.target.value)}
            />
          </div>

          <div className="refund-toggle-row">
            <label className="refund-toggle-label">
              <input
                type="checkbox"
                checked={reverseTransfer}
                onChange={e => setReverseTransfer(e.target.checked)}
              />
              <span>Reverse provider Stripe transfer <small>(check if payout was already sent)</small></span>
            </label>
          </div>

          <div className="refund-info-box">
            <FaInfoCircle className="info-icon" />
            <p>
              Clawbacks are now <strong>dynamically calculated</strong> based on the booking's actual commission rate. 
              The system will automatically protect your platform fees during the reversal.
            </p>
          </div>

          <div className="refund-stripe-notice">
            <FaStripe className="stripe-icon" />
            <span>This will trigger a real Stripe <code>refunds.create</code> API call.</span>
          </div>
        </div>

        <div className="refund-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-approve" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Processing...' : 'Approve & Process Refund'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Reject Modal ──────────────────────────────────────────────
const RejectModal = ({ refund, onClose, onConfirm }) => {
  const [adminNote, setAdminNote] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!adminNote.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setLoading(true);
    await onConfirm({ adminNote });
    setLoading(false);
  };

  return (
    <div className="refund-modal-overlay" onClick={onClose}>
      <div className="refund-modal" onClick={e => e.stopPropagation()}>
        <div className="refund-modal-header">
          <div className="refund-modal-title">
            <FaTimesCircle className="modal-icon reject" />
            <h3>Reject Refund Request</h3>
          </div>
          <button className="refund-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="refund-modal-body">
          <div className="refund-detail-row">
            <span>Booking #</span>
            <strong>{refund?.booking?.bookingNumber || refund?.bookingId}</strong>
          </div>
          <div className="refund-detail-row">
            <span>Customer's Reason</span>
            <em>{refund?.reason}</em>
          </div>

          <div className="form-group mt-4">
            <label>Rejection Reason (shown to customer) *</label>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Explain why the refund is being rejected..."
              value={adminNote}
              onChange={e => setAdminNote(e.target.value)}
            />
          </div>
        </div>

        <div className="refund-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-reject" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Rejecting...' : 'Reject Request'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Detail Panel ──────────────────────────────────────────────
const RefundDetailPanel = ({ refund, onClose, onApprove, onReject }) => {
  if (!refund) return null;

  const timeline = [
    { label: 'Request Submitted', date: refund.requestedAt || refund.createdAt, done: true },
    { label: 'Admin Review', date: refund.resolvedAt, done: !!refund.resolvedAt },
    { label: 'Stripe Processing', date: refund.stripeRefundId ? refund.resolvedAt : null, done: !!refund.stripeRefundId },
    { label: 'Refund Completed', date: refund.completedAt, done: refund.status === 'COMPLETED' },
  ];

  const totalPaid = refund?.booking?.totalAmount || refund?.requestedAmount || 0;

  return (
    <div className="refund-detail-panel">
      <div className="detail-panel-header">
        <h3>Refund Details</h3>
        <button className="detail-panel-close" onClick={onClose}>×</button>
      </div>

      <div className="detail-panel-body">
        <div className="detail-status-row">
          <RefundStatusBadge status={refund.status} />
          <RefundTypeBadge type={refund.refundType} />
        </div>

        <div className="detail-section">
          <h4><FaCalendarAlt /> Booking Info</h4>
          <div className="detail-grid">
            <div><span>Booking #</span><strong>{refund?.booking?.bookingNumber || refund?.bookingId}</strong></div>
            <div><span>Scheduled Date</span><strong>{refund?.booking?.scheduledDate || '—'}</strong></div>
            <div><span>Total Amount Paid</span><strong className="amount-green">${Number(totalPaid).toFixed(2)}</strong></div>
            <div><span>Refund Requested</span><strong>${Number(refund.requestedAmount || totalPaid).toFixed(2)}</strong></div>
            {refund.approvedAmount && (
              <div><span>Approved Amount</span><strong className="amount-green">${Number(refund.approvedAmount).toFixed(2)}</strong></div>
            )}
          </div>
        </div>

        <div className="detail-section">
          <h4><FaUser /> Customer</h4>
          <div className="detail-grid">
            <div><span>Name</span><strong>{refund?.customer?.firstName} {refund?.customer?.lastName}</strong></div>
            <div><span>Email</span><strong>{refund?.customer?.email}</strong></div>
          </div>
        </div>

        <div className="detail-section">
          <h4><FaUserNurse /> Provider</h4>
          <div className="detail-grid">
            <div><span>Name</span><strong>{refund?.provider?.firstName} {refund?.provider?.lastName}</strong></div>
            <div><span>Email</span><strong>{refund?.provider?.email}</strong></div>
          </div>
        </div>

        <div className="detail-section">
          <h4><FaInfoCircle /> Refund Reason</h4>
          <p className="refund-reason-text">{refund.reason}</p>
          {refund.adminNote && (
            <>
              <h4>Admin Note</h4>
              <p className="refund-admin-note">{refund.adminNote}</p>
            </>
          )}
        </div>

        {(refund.stripeRefundId || refund.stripeReversalId) && (
          <div className="detail-section stripe-ids">
            <h4><FaStripe /> Stripe IDs</h4>
            {refund.stripeRefundId && <div><span>Refund ID</span><code>{refund.stripeRefundId}</code></div>}
            {refund.stripeReversalId && <div><span>Reversal ID</span><code>{refund.stripeReversalId}</code></div>}
          </div>
        )}

        {/* Timeline */}
        <div className="detail-section">
          <h4>Refund Timeline</h4>
          <div className="refund-timeline">
            {timeline.map((step, i) => (
              <div key={i} className={`timeline-step ${step.done ? 'done' : ''}`}>
                <div className="timeline-dot" />
                <div className="timeline-info">
                  <strong>{step.label}</strong>
                  <span>{step.date ? new Date(step.date).toLocaleString() : 'Pending'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        {refund.status === 'PENDING' && (
          <div className="detail-actions">
            <button className="btn btn-approve" onClick={() => onApprove(refund)}>
              <FaCheckCircle /> Approve Refund
            </button>
            <button className="btn btn-reject" onClick={() => onReject(refund)}>
              <FaTimesCircle /> Reject Request
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────
const Refunds = () => {
  const [refunds, setRefunds] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ALL');
  const [search, setSearch] = useState('');
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 15, totalPages: 1 });

  const TABS = [
    { key: 'ALL', label: 'All Requests' },
    { key: 'PENDING', label: 'Pending' },
    { key: 'APPROVED', label: 'Approved' },
    { key: 'COMPLETED', label: 'Completed' },
    { key: 'REJECTED', label: 'Rejected' },
    { key: 'FAILED', label: 'Failed' },
  ];

  useEffect(() => {
    fetchStats();
    fetchRefunds();
  }, [activeTab, pagination.page]);

  const fetchStats = async () => {
    try {
      const res = await api.get(ENDPOINTS.REFUNDS.stats);
      setStats(res.data || res);
    } catch (err) {
      console.error('Failed to fetch refund stats:', err);
    }
  };

  const fetchRefunds = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (activeTab !== 'ALL') params.set('status', activeTab);

      const res = await api.get(`${ENDPOINTS.REFUNDS.base}?${params}`);
      const data = res.data || res;
      setRefunds(data.data || []);
      setPagination(prev => ({ ...prev, ...data.meta }));
    } catch (err) {
      console.error('Failed to fetch refunds:', err);
      toast.error('Failed to load refunds');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (dto) => {
    try {
      await api.patch(`${ENDPOINTS.REFUNDS.base}/${approveTarget.id}/approve`, dto);
      toast.success('Refund approved and Stripe refund triggered!');
      setApproveTarget(null);
      setSelectedRefund(null);
      fetchRefunds();
      fetchStats();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleReject = async (dto) => {
    try {
      await api.patch(`${ENDPOINTS.REFUNDS.base}/${rejectTarget.id}/reject`, dto);
      toast.success('Refund request rejected.');
      setRejectTarget(null);
      setSelectedRefund(null);
      fetchRefunds();
      fetchStats();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const filteredRefunds = refunds.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.booking?.bookingNumber?.toLowerCase().includes(s) ||
      r.customer?.firstName?.toLowerCase().includes(s) ||
      r.customer?.lastName?.toLowerCase().includes(s) ||
      r.customer?.email?.toLowerCase().includes(s) ||
      r.reason?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="refunds-page">
      {/* ── Header ── */}
      <div className="refunds-header">
        <div className="refunds-header-left">
          <div className="refunds-title-row">
            <FaUndoAlt className="refunds-title-icon" />
            <div>
              <h1>Refund Management</h1>
              <p>Review, approve, and track all customer refund requests</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats Cards ── */}
      <div className="refunds-stats-grid">
        {[
          { label: 'Total Requests', value: stats.total ?? 0, color: '#6366f1' },
          { label: 'Pending Review', value: stats.pending ?? 0, color: '#f59e0b', urgent: true },
          { label: 'Approved', value: stats.approved ?? 0, color: '#3b82f6' },
          { label: 'Completed', value: stats.completed ?? 0, color: '#10b981' },
          { label: 'Rejected', value: stats.rejected ?? 0, color: '#ef4444' },
          { label: 'Failed', value: stats.failed ?? 0, color: '#dc2626' },
        ].map(s => (
          <div key={s.label} className={`refund-stat-card ${s.urgent && s.value > 0 ? 'urgent' : ''}`}>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Main Content ── */}
      <div className={`refunds-content ${selectedRefund ? 'with-panel' : ''}`}>
        <div className="refunds-table-section">
          {/* Tabs */}
          <div className="refunds-tabs">
            {TABS.map(tab => (
              <button
                key={tab.key}
                className={`refund-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => { setActiveTab(tab.key); setPagination(p => ({ ...p, page: 1 })); }}
              >
                {tab.label}
                {tab.key === 'PENDING' && stats.pending > 0 && (
                  <span className="tab-badge">{stats.pending}</span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="refunds-search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by booking #, customer name, or reason..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="refunds-search-input"
            />
          </div>

          {/* Table */}
          {loading ? (
            <div className="loading-state" style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div className="spinner" />
              <p style={{ marginTop: '20px', color: '#636e72', fontWeight: '500' }}>Loading refund requests...</p>
            </div>
          ) : filteredRefunds.length === 0 ? (
            <div className="refunds-empty">
              <FaUndoAlt />
              <p>No refund requests found</p>
            </div>
          ) : (
            <div className="refunds-table-wrapper">
              <table className="refunds-table">
                <thead>
                  <tr>
                    <th>Booking #</th>
                    <th>Customer</th>
                    <th>Provider</th>
                    <th>Amount Requested</th>
                    <th>Refund Type</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRefunds.map(refund => (
                    <tr
                      key={refund.id}
                      className={`refund-row ${selectedRefund?.id === refund.id ? 'selected' : ''}`}
                      onClick={() => setSelectedRefund(refund)}
                    >
                      <td>
                        <span className="booking-num">
                          #{refund.booking?.bookingNumber || refund.bookingId}
                        </span>
                      </td>
                      <td>
                        <div className="person-cell">
                          <div className="person-avatar customer-avatar">
                            {refund.customer?.firstName?.[0]}{refund.customer?.lastName?.[0]}
                          </div>
                          <div>
                            <div>{refund.customer?.firstName} {refund.customer?.lastName}</div>
                            <small>{refund.customer?.email}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="person-cell">
                          <div className="person-avatar provider-avatar">
                            {refund.provider?.firstName?.[0]}{refund.provider?.lastName?.[0]}
                          </div>
                          <div>
                            <div>{refund.provider?.firstName} {refund.provider?.lastName}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="amount-cell">
                          ${Number(refund.requestedAmount || refund.booking?.totalAmount || 0).toFixed(2)}
                          {refund.approvedAmount && (
                            <small className="approved-amount">
                              → ${Number(refund.approvedAmount).toFixed(2)} approved
                            </small>
                          )}
                        </span>
                      </td>
                      <td><RefundTypeBadge type={refund.refundType} /></td>
                      <td><RefundStatusBadge status={refund.status} /></td>
                      <td>
                        <span className="date-cell">
                          {new Date(refund.createdAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        {refund.status === 'PENDING' && (
                          <div className="action-btns">
                            <button
                              className="action-btn approve-btn"
                              onClick={() => { setApproveTarget(refund); }}
                              title="Approve Refund"
                            >
                              <FaCheckCircle />
                            </button>
                            <button
                              className="action-btn reject-btn"
                              onClick={() => { setRejectTarget(refund); }}
                              title="Reject Request"
                            >
                              <FaTimesCircle />
                            </button>
                          </div>
                        )}
                        {refund.status !== 'PENDING' && (
                          <button
                            className="action-btn view-btn"
                            onClick={() => setSelectedRefund(refund)}
                            title="View Details"
                          >
                            <FaInfoCircle />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="refunds-pagination">
              <button
                className="page-btn"
                disabled={pagination.page <= 1}
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              >
                ← Prev
              </button>
              <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)</span>
              <button
                className="page-btn"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* ── Detail Panel ── */}
        {selectedRefund && (
          <RefundDetailPanel
            refund={selectedRefund}
            onClose={() => setSelectedRefund(null)}
            onApprove={(r) => { setApproveTarget(r); }}
            onReject={(r) => { setRejectTarget(r); }}
          />
        )}
      </div>

      {/* ── Modals ── */}
      {approveTarget && (
        <ApproveModal
          refund={approveTarget}
          onClose={() => setApproveTarget(null)}
          onConfirm={handleApprove}
        />
      )}

      {rejectTarget && (
        <RejectModal
          refund={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onConfirm={handleReject}
        />
      )}
    </div>
  );
};

export default Refunds;
